import {
  Banner,
  GreyGrey30,
  GreyGrey70,
  GreyGrey80,
  GreyGrey90,
  IconArrowRight,
  Image,
  Modal,
  Primary13Regular,
  Primary13SemiBold,
  StatTile,
  useNotifications,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { Footer } from '../common/Footer'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import { useEndOfCurrentBillingPeriod } from '../../../../../../utils/useEndOfNextBillingPeriod'
import { CancelSelection } from '../../scheduleShortcuts.types'
import { ReactNode, useMemo } from 'react'
import { match } from 'ts-pattern'
import { useMutation, useQuery } from '@sequencehq/api'
import { useBillingScheduleAggregate } from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import { format, isAfter, isBefore, parse } from '@sequencehq/utils/dates'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { getLogoSrc } from 'Customer/index'
import { parseBillingSchedulePhaseDuration } from '@sequencehq/utils'
import { getApiErrorMessage } from 'Cube/utils/getApiErrorMessage'
import {
  CANCEL_MODAL_MIN_HEIGHT_PX,
  DATE_FORMAT_WITH_YEARS
} from '../../scheduleShortcuts.constants'
import { useCurrentPhaseHasInAdvancePrices } from '../../../../../../utils/useCurrentPhaseHasInAdvancePrices'
import { apiDatesAdapters } from '@sequencehq/utils/dates'
import { API_DATE_FORMAT } from 'BillingScheduleHome/billingScheduleHome.constants'

const useCancelConfirmationScreen = (props: {
  cancelSelection: CancelSelection
  onClose: () => void
}) => {
  const { displayNotification } = useNotifications()
  const cancelDateSelection = props.cancelSelection
  const billingScheduleId = useBillingScheduleId()
  const { endOfBillingPeriod, isLoading: endOfBillingPeriodLoading } =
    useEndOfCurrentBillingPeriod({
      billingScheduleId
    })
  const {
    isLoading: currentPhaseInAdvanceLoading,
    currentPhaseHasInAdvancePrices
  } = useCurrentPhaseHasInAdvancePrices({
    billingScheduleId
  })

  /**
   * We could do with the ability to cancel without archiving, but ho-hum
   */
  const handleMutationFailure = (error?: unknown) => {
    displayNotification(
      getApiErrorMessage(error, 'Could not update schedule end date'),
      { type: 'error' }
    )
  }

  const putScheduleMutator = useMutation(
    dashboard20240730Client.putBillingSchedule,
    {
      onSuccess: () => {
        displayNotification(`Schedule end date updated`, {
          type: 'success'
        })
        props.onClose()
      },
      onError: handleMutationFailure
    }
  )

  /**
   * We need the raw billing schedule data for the PUT, and this is
   * the most simple way to acquire it, rather than parsing
   * backwards from the dates in the aggregate (which is much more
   * useful to every other scenario beyond PUT)
   */
  const billingScheduleQuery = useQuery(
    dashboard20240730Client.getBillingSchedule,
    {
      id: billingScheduleId
    }
  )
  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId,
    select: data => {
      const phaseDurations = data.billingSchedule.phases.map(phase => {
        const startDate = phase.startDate
        const endDate = phase.endDate

        const duration = parseBillingSchedulePhaseDuration({
          startDate,
          endDate
        })

        return duration
      })

      return {
        startDate: data.billingSchedule.startDate,
        endDate:
          data.billingSchedule.endDate &&
          !phaseDurations.find(d => d === 'MILESTONE')
            ? data.billingSchedule.endDate
            : undefined,
        customerId: data.customer?.id,
        customerDomain: data.customer?.domain,
        customerName: data.customer?.legalName,
        phases: data.billingSchedule.phases
      }
    }
  })

  const updateScheduleToNewEndDate = async (newEndDate: Date) => {
    if (!billingScheduleQuery.data) {
      handleMutationFailure()
      return
    }

    const phasesWithParsedDates = billingScheduleQuery.data.phases.map(
      phase => ({
        ...phase,
        startDate: parse(phase.startDate, API_DATE_FORMAT, new Date()),
        endDate: phase.endDate
          ? parse(phase.endDate, API_DATE_FORMAT, new Date())
          : undefined
      })
    )
    const newFinalPhase = phasesWithParsedDates.find(p => {
      if (!p.startDate) {
        return
      }

      return (
        isBefore(p.startDate, newEndDate) &&
        (!p.endDate || isAfter(p.endDate, newEndDate))
      )
    })

    if (!newFinalPhase) {
      handleMutationFailure()
      return
    }

    const newFinalPhaseIndex =
      billingScheduleAggregateQuery.data?.phases.findIndex(
        p => p.id === newFinalPhase.id
      )

    const { customFields: _customFields, ...scheduleBody } =
      billingScheduleQuery.data
    putScheduleMutator.mutate({
      id: billingScheduleId,
      body: {
        ...scheduleBody,
        endDate: apiDatesAdapters.toApi(newEndDate),
        phases: [
          ...billingScheduleQuery.data.phases.slice(0, newFinalPhaseIndex),
          {
            ...newFinalPhase,
            startDate: apiDatesAdapters.toApi(newFinalPhase.startDate),
            endDate: apiDatesAdapters.toApi(newEndDate)
          }
        ]
      }
    })
  }

  const newEndDate = useMemo(() => {
    return match(cancelDateSelection)
      .with({ cancelDateType: 'immediate' }, () => new Date())
      .with({ cancelDateType: 'endOfBillingPeriod' }, () => endOfBillingPeriod)
      .with({ cancelDateType: 'customDate' }, ({ customDate }) => customDate)
      .exhaustive()
  }, [cancelDateSelection, endOfBillingPeriod])

  const onSubmit = () => {
    if (!newEndDate) {
      return
    }

    updateScheduleToNewEndDate(newEndDate)
  }

  return {
    cancelType: cancelDateSelection.cancelDateType,
    currentPhaseHasInAdvancePrices,
    isLoading:
      billingScheduleAggregateQuery.isLoading ||
      endOfBillingPeriodLoading ||
      currentPhaseInAdvanceLoading,
    customer: {
      id: billingScheduleAggregateQuery.data?.customerId,
      name: billingScheduleAggregateQuery.data?.customerName,
      logoSrc: getLogoSrc({
        domain: billingScheduleAggregateQuery.data?.customerDomain
      })
    },
    startDate: billingScheduleAggregateQuery.data?.startDate,
    originalEndDate: billingScheduleAggregateQuery.data?.endDate,
    newEndDate,
    onSubmit,
    submitting: putScheduleMutator.isPending
  }
}

const DateDisplay = (props: {
  label: string
  date: Date | undefined
  'data-testid'?: string
}) => {
  return (
    <StatTile
      label={props.label}
      value={
        props.date ? format(props.date, DATE_FORMAT_WITH_YEARS) : 'Ongoing'
      }
      data-testid={props['data-testid']}
    />
  )
}

const DetailRow = (props: { label: ReactNode; value: ReactNode }) => {
  return (
    <Grid
      gridTemplateColumns="auto 1fr"
      {...Primary13Regular}
      height="32px"
      alignItems="center"
      _notLast={{
        borderBottom: `1px solid ${GreyGrey30}`
      }}
    >
      <GridItem color={GreyGrey70} height="fit-content" userSelect="none">
        {props.label}
      </GridItem>
      <Flex height="fit-content" justifyContent="flex-end" color={GreyGrey90}>
        {props.value}
      </Flex>
    </Grid>
  )
}

export const CancelConfirmationScreen = (props: {
  cancelSelection: CancelSelection
  onBack: () => void
  onClose: () => void
}) => {
  const {
    cancelType,
    currentPhaseHasInAdvancePrices,
    customer,
    originalEndDate,
    newEndDate,
    onSubmit,
    submitting
  } = useCancelConfirmationScreen({
    cancelSelection: props.cancelSelection,
    onClose: props.onClose
  })

  return (
    <>
      <Modal.Content>
        <Grid
          gridTemplateAreas={`
          "originalEndDate dateChangeArrow newEndDate"
          "details details details"
          "description description description"`}
          gridTemplateColumns="1fr auto 1fr"
          gridTemplateRows="auto auto 1fr"
          rowGap="16px"
          columnGap="16px"
          minHeight={`${CANCEL_MODAL_MIN_HEIGHT_PX}px`}
        >
          <GridItem gridArea="originalEndDate">
            <DateDisplay
              label="Original end date"
              date={originalEndDate}
              data-testid="cancelConfirmation.originalEndDate"
            />
          </GridItem>
          <GridItem
            gridArea="dateChangeArrow"
            height="100%"
            display="flex"
            alignItems="center"
          >
            <IconArrowRight color={GreyGrey80} />
          </GridItem>
          <GridItem gridArea="newEndDate">
            <DateDisplay
              label="New end date"
              date={newEndDate}
              data-testid="cancelConfirmation.newEndDate"
            />
          </GridItem>
          <GridItem gridArea="details">
            <DetailRow
              label={'Customer'}
              value={
                <Flex gap="6px">
                  {customer.logoSrc && (
                    <Image src={customer.logoSrc} height="16px" width="16px" />
                  )}
                  {customer.name}
                </Flex>
              }
            />
            <DetailRow
              label={'Schedule ends'}
              value={
                <Flex {...Primary13Regular} color={GreyGrey90}>
                  {match(cancelType)
                    .with('immediate', () => 'Today')
                    .with('endOfBillingPeriod', () => 'End of billing period')
                    .with('customDate', () => 'Custom end date')
                    .exhaustive()}
                </Flex>
              }
            />
          </GridItem>
          <GridItem gridArea="description">
            <Banner variant="neutral">
              <Box as="span" {...Primary13SemiBold}>
                {match({
                  cancelType,
                  currentPhaseHasInAdvancePrices
                })
                  .with(
                    {
                      cancelType: 'endOfBillingPeriod'
                    },
                    () => null
                  )
                  .with(
                    {
                      currentPhaseHasInAdvancePrices: true
                    },
                    () => <>Charges and refunds: </>
                  )
                  .with(
                    {
                      currentPhaseHasInAdvancePrices: false
                    },
                    () => <>Prorated charges: </>
                  )
                  .exhaustive()}
              </Box>
              {match({
                cancelType,
                currentPhaseHasInAdvancePrices
              })
                .with(
                  {
                    cancelType: 'immediate',
                    currentPhaseHasInAdvancePrices: false
                  },
                  () =>
                    "A final invoice will be created today, with pro-rated charges up to the schedule's end date"
                )
                .with(
                  {
                    cancelType: 'immediate',
                    currentPhaseHasInAdvancePrices: true
                  },
                  () =>
                    'A final invoice will be created today, along with a credit note refunding any prepaid fees.'
                )
                .with(
                  {
                    cancelType: 'endOfBillingPeriod'
                  },
                  () => 'No pro-rated charges or refunds are created.'
                )
                .with(
                  {
                    cancelType: 'customDate',
                    currentPhaseHasInAdvancePrices: false
                  },
                  () =>
                    "A final invoice will be created, with pro-rated charges up to the schedule's end date"
                )
                .with(
                  {
                    cancelType: 'customDate',
                    currentPhaseHasInAdvancePrices: true
                  },
                  () =>
                    'A final invoice will be created, along with a credit note refunding any prepaid fees'
                )
                .exhaustive()}
            </Banner>
          </GridItem>
        </Grid>
      </Modal.Content>
      <Footer
        onBack={props.onBack}
        onContinue={onSubmit}
        disabled={!newEndDate}
        destructive={cancelType === 'immediate'}
        submitting={submitting}
        submitButtonText={submitting ? 'Ending schedule...' : 'End schedule'}
      />
    </>
  )
}
